/**
 * E-Prescription Custom Hooks
 *
 * Business logic hooks for e-prescription management.
 */

import { useMemo } from 'react';

import { useGetEPrescriptionStatusQuery, useGetRegistrationStatusQuery } from './api';

interface UseEPrescriptionStatusOptions {
  /** Optional patient ID to check patient connection status */
  patientId?: string;
  /** Skip the query (useful when patientId is not yet available) */
  skip?: boolean;
}

/**
 * Hook for e-prescription connection status
 *
 * Provides convenient access to organization, user, and optionally patient connection status.
 * When patientId is provided, also returns whether that patient is connected to DoseSpot.
 */
export function useEPrescriptionStatus(options: UseEPrescriptionStatusOptions = {}) {
  const { patientId, skip = false } = options;

  const {
    data: status,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetEPrescriptionStatusQuery(
    { patientId: patientId || undefined },
    { skip }
  );

  return useMemo(
    () => ({
      /** Whether the organization has a DoseSpot clinic connected */
      isOrgConnected: status?.organization_connected ?? false,
      /** Whether the user is registered as a DoseSpot clinician */
      isUserConnected: status?.user_connected ?? false,
      /** Whether the patient is connected to DoseSpot (only available if patientId provided) */
      isPatientConnected: status?.patient_connected ?? false,
      /** Loading state */
      isLoading,
      /** Error state */
      isError,
      /** Error object if present */
      error,
      /** Refetch function */
      refetch,
    }),
    [status, isLoading, isError, error, refetch]
  );
}

interface UseRegistrationStatusOptions {
  /** Skip the query (should be true unless user is in 'registered' state) */
  skip?: boolean;
}

/**
 * Hook for checking identity proofing / registration completion status.
 * Should only be called when the user is already in 'registered' state.
 */
export function useRegistrationStatus(options: UseRegistrationStatusOptions = {}) {
  const { skip = false } = options;

  const { data, isLoading, isError } = useGetRegistrationStatusQuery(undefined, { skip });

  return useMemo(
    () => ({
      isIdentityProofingComplete: data?.identity_proofing_complete ?? false,
      registrationStatus: data?.registration_status ?? null,
      isLoading,
      isError,
    }),
    [data, isLoading, isError],
  );
}
